@if (isNetwork()) {
  <div class="space-y-6">
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Monatsübersicht</h2>
        <p hlmCardDescription>
          Verkäufe, Einlösungen und was du deinen Mitgliedern schuldest. Am 1. jedes Monats bekommst
          du die Übersicht des Vormonats per E-Mail, die Abrechnung liegt dann als Entwurf bereit.
        </p>
      </div>
      <div hlmCardContent class="space-y-4">
        <div class="flex flex-wrap items-end gap-2">
          <div class="flex flex-col gap-1.5">
            <label hlmFieldLabel for="period">Monat</label>
            <hlm-native-select
              selectId="period"
              [ngModel]="selectedPeriod()"
              (ngModelChange)="selectPeriod($event)">
              @for (p of periods; track p.value) {
                <option hlmNativeSelectOption [value]="p.value">{{ p.label }}</option>
              }
            </hlm-native-select>
          </div>
          <button
            hlmBtn
            variant="outline"
            [disabled]="busy() === 'report-pdf' || !report() || isRunningMonth()"
            [attr.aria-describedby]="isRunningMonth() ? 'report-running-hint' : null"
            (click)="downloadReport('pdf')">
            <ng-icon name="lucideDownload" />
            PDF
          </button>
          <button
            hlmBtn
            variant="outline"
            [disabled]="busy() === 'report-csv' || !report() || isRunningMonth()"
            [attr.aria-describedby]="isRunningMonth() ? 'report-running-hint' : null"
            (click)="downloadReport('csv')">
            <ng-icon name="lucideDownload" />
            CSV (Excel)
          </button>
        </div>
        @if (isRunningMonth()) {
          <p hlmMuted id="report-running-hint" class="text-xs">
            Die Übersicht zum Herunterladen gibt es nach Monatsende – bis dahin können sich die
            Zahlen noch ändern.
          </p>
        }

        @if (error()) {
          <div hlmAlert variant="destructive">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertTitle>{{ error() }}</p>
          </div>
        }

        @if (loading()) {
          <hlm-spinner />
        } @else if (report(); as r) {
          <div class="grid gap-3 sm:grid-cols-4">
            <div class="rounded-md border p-3">
              <p hlmMuted class="text-xs">Verkauft</p>
              <p class="text-lg font-semibold">{{ r.sales.count }}</p>
              <p hlmMuted class="text-xs">{{ euro(r.sales.face_value_cents) }}</p>
            </div>
            <div class="rounded-md border p-3">
              <p hlmMuted class="text-xs">Erlös nach Gebühren</p>
              <p class="text-lg font-semibold">{{ euro(r.sales.net_cents) }}</p>
            </div>
            <div class="rounded-md border p-3">
              <p hlmMuted class="text-xs">Eingelöst bei Mitgliedern</p>
              <p class="text-lg font-semibold">
                {{ euro(r.member_redemptions_total.value_cents) }}
              </p>
            </div>
            <div class="rounded-md border p-3">
              <p hlmMuted class="text-xs">Auszahlung an Mitglieder</p>
              <p class="text-lg font-semibold">{{ euro(r.member_payout_total_cents) }}</p>
            </div>
          </div>

          <div hlmTableContainer>
            <table hlmTable>
              <thead hlmTHead>
                <tr hlmTr>
                  <th hlmTh>Einlösestelle</th>
                  <th hlmTh class="text-right">Anzahl</th>
                  <th hlmTh class="text-right">Einlösewert</th>
                  <th hlmTh class="text-right">Auszahlung</th>
                </tr>
              </thead>
              <tbody hlmTBody>
                @for (row of r.redeemed_by_members; track row.member_company_id) {
                  <tr hlmTr>
                    <td hlmTd>
                      {{ row.member_name || row.member_company_id }}
                      @if (!row.has_bank_account) {
                        <span hlmBadge variant="destructive" class="ms-1">keine IBAN</span>
                      }
                    </td>
                    <td hlmTd class="text-right tabular-nums">{{ row.count }}</td>
                    <td hlmTd class="text-right tabular-nums">{{ euro(row.face_value_cents) }}</td>
                    <td hlmTd class="text-right tabular-nums">{{ euro(row.payout_cents) }}</td>
                  </tr>
                } @empty {
                  <tr hlmTr>
                    <td hlmTd colspan="4" class="text-muted-foreground text-center">
                      In diesem Monat wurde bei keinem Mitglied eingelöst.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <p hlmMuted class="text-xs">
            Offener Gutscheinwert am Monatsende: {{ euro(r.outstanding_paid_cents) }} (verkauft),
            {{ euro(r.outstanding_internal_cents) }} (intern).
          </p>
        }
      </div>
    </section>

    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Abrechnung mit Mitgliedern</h2>
        <p hlmCardDescription>
          Du überweist deinen Mitgliedern ihre Einlösungen selbst – mit einer Sammelüberweisung, die
          du bei deiner Bank hochlädst. Jede Einlösung wird genau einmal abgerechnet.
        </p>
      </div>
      <div hlmCardContent class="space-y-4">
        @if (!company()?.has_bank_account) {
          <div hlmAlert>
            <ng-icon name="lucideLandmark" />
            <p hlmAlertTitle>Bankverbindung fehlt</p>
            <p hlmAlertDescription>
              Für die SEPA-Datei brauchen wir das Konto, von dem du überweist.
              <a
                class="underline underline-offset-4"
                [routerLink]="['/settings']"
                [queryParams]="{ tab: 'bank-account' }"
                >Jetzt hinterlegen</a
              >
            </p>
          </div>
        }

        @if (skipped().length) {
          <div hlmAlert variant="warning">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertTitle>Noch ohne IBAN: {{ skipped().join(', ') }}</p>
            <p hlmAlertDescription>
              Ihre Einlösungen bleiben offen und kommen in die nächste Abrechnung.
            </p>
          </div>
        }

        @if (settlement(); as s) {
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p class="font-medium">{{ s.period_label }} · {{ euro(s.total_payout_cents) }}</p>
              <p hlmMuted class="text-xs">
                {{ s.lines.length }} {{ s.lines.length === 1 ? 'Mitglied' : 'Mitglieder' }} ·
                {{ s.redemption_count }} Einlösungen
                @if (s.created_automatically) {
                  · automatisch erstellt
                }
              </p>
            </div>
            <span hlmBadge [variant]="s.status === 'paid' ? 'solid' : 'outline'">
              {{ statusLabel(s.status) }}
            </span>
          </div>

          <div hlmTableContainer>
            <table hlmTable>
              <thead hlmTHead>
                <tr hlmTr>
                  <th hlmTh>Mitglied</th>
                  <th hlmTh>Konto</th>
                  <th hlmTh class="text-right">Einlösungen</th>
                  <th hlmTh class="text-right">Betrag</th>
                </tr>
              </thead>
              <tbody hlmTBody>
                @for (line of s.lines; track line.member_company_id) {
                  <tr hlmTr>
                    <td hlmTd>{{ line.member_name || line.member_company_id }}</td>
                    <td hlmTd>
                      <span class="font-mono text-xs">{{ line.iban_masked }}</span>
                      <p hlmMuted class="text-xs">{{ line.account_holder }}</p>
                      @if (line.iban_changed_at) {
                        <span hlmBadge variant="destructive"
                          >IBAN kürzlich geändert – bitte prüfen</span
                        >
                      }
                    </td>
                    <td hlmTd class="text-right tabular-nums">{{ line.redemption_count }}</td>
                    <td hlmTd class="text-right tabular-nums">{{ euro(line.payout_cents) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (s.status !== 'paid') {
            <div class="flex flex-wrap items-end gap-2">
              <div hlmField class="min-w-72 flex-1">
                <label hlmFieldLabel for="export-format">Format</label>
                <hlm-native-select selectId="export-format" [(ngModel)]="exportFormat">
                  @for (f of exportFormats; track f.value) {
                    <option hlmNativeSelectOption [value]="f.value">{{ f.label }}</option>
                  }
                </hlm-native-select>
              </div>
              <div hlmField>
                <label hlmFieldLabel for="execution-date">Ausführung am</label>
                <input hlmInput id="execution-date" type="date" [(ngModel)]="executionDate" />
              </div>
              <!-- The network's own account is the debtor in the file: no IBAN, no file. -->
              <button
                hlmBtn
                [disabled]="busy() === 'export' || !company()?.has_bank_account"
                (click)="exportSettlement(s)">
                <ng-icon name="lucideDownload" />
                Sammelüberweisung herunterladen
              </button>
            </div>
            @if (!company()?.has_bank_account) {
              <p hlmMuted class="text-xs">
                Der Download ist möglich, sobald deine Bankverbindung hinterlegt ist.
              </p>
            }
            <div class="flex flex-wrap gap-2">
              <button
                hlmBtn
                variant="outline"
                [disabled]="busy() === 'paid' || s.status === 'draft'"
                (click)="markPaid(s)">
                <ng-icon name="lucideCircleCheck" />
                Überweisung ausgeführt – Mitglieder benachrichtigen
              </button>
              <button hlmBtn variant="ghost" [disabled]="busy() === 'cancel'" (click)="cancel(s)">
                Stornieren
              </button>
            </div>
          } @else {
            <p hlmMuted class="text-sm">
              Bezahlt – deine Mitglieder wurden per E-Mail benachrichtigt.
            </p>
          }
        } @else if (isRunningMonth()) {
          <p hlmMuted class="text-sm">
            Abgerechnet wird nach Monatsende. Die Abrechnung für {{ report()?.period_label }} liegt
            am 1. als Entwurf bereit.
          </p>
        } @else {
          <p hlmMuted class="text-sm">
            Für diesen Monat gibt es noch keine Abrechnung. Sie enthält alle noch offenen
            Einlösungen deiner Mitglieder bis Monatsende.
          </p>
          <button hlmBtn [disabled]="busy() === 'create'" (click)="createSettlement()">
            @if (busy() === 'create') {
              <hlm-spinner />
            }
            Abrechnung erstellen
          </button>
        }
      </div>
    </section>
  </div>
}
